<template>
  <div class="article-detail-container">

    <BlogHeader  @search="handleSearchFromHeader" />

    <main class="main-content" v-if="article">
      <div class="article-content-col">
        <article class="article-content">
        <h1 class="article-title">{{ article.title }}</h1>
        
        <div class="article-meta">
          <div class="meta-row">
            <span class="time">{{ formatDate(article.createdAt) }}</span>
            <span class="read-count">
              <i class="el-icon-view action-icon"></i>{{ article.readCount }}
            </span>
          </div>

          <div class="meta-row">
            <span class="category" @click="goCategory(article.category.id)">
              {{ article.category?.name }}
            </span>
            <el-tag 
              v-for="tag in articleTags" 
              :key="tag.id" 
              size="mini"
              @click="goTag(tag.id)"
            >
              #{{ tag.name }}
            </el-tag>
          </div>
        </div>

        <div class="article-body" v-html="renderedContent"></div>

        <div class="article-actions">
          <div class="action-buttons">
            <button 
              class="action-btn" 
              :class="{ liked: isLiked }"
              @click="handleLike"
            >
              <i class="like-icon"></i>
              <span>{{ article.likeCount }}</span>
            </button>
            <!-- <button class="action-btn" @click="handleFavorite">
              <i class="el-icon-collection" style="margin-right: 2px;"></i>
              <span>收藏</span>
            </button> -->
            <button class="action-btn" @click="handleComment">
              <i class="el-icon-chat-dot-square action-icon"></i>
              <span>{{ totalComments }}</span>
            </button>
            <button class="action-btn" @click="handleShare">
              <i class="el-icon-share action-icon"></i>
              <span>分享</span>
            </button>
          </div>
        </div>

        <div class="article-nav">
          <div v-if="prevArticle" class="nav-item prev">
            <span class="nav-label">上一篇</span>
            <a @click="goArticle(prevArticle.id)">{{ prevArticle.title }}</a>
          </div>
          <div v-if="nextArticle" class="nav-item next">
            <span class="nav-label">下一篇</span>
            <a @click="goArticle(nextArticle.id)">{{ nextArticle.title }}</a>
          </div>
        </div>
      </article>

      <section class="comment-section" v-if="article">
          <div class="comment-container">
        <h3>评论列表</h3>
        
        <div class="comment-input">
          <el-input 
            v-model="commentContent" 
            placeholder="写下你的评论..." 
            :rows="3"
            type="textarea"
          ></el-input>
          <div class="submit-row">
            <label class="anonymous-checkbox">
              <input type="checkbox" v-model="isAnonymous"> 匿名评论
            </label>
            <el-button type="primary" @click="submitComment">发表评论</el-button>
          </div>
        </div>

        <div v-if="comments.length > 0">
          <div 
            v-for="comment in comments" 
            :key="comment.id" 
            class="comment-item"
          >
            <div class="comment-header">
              <span class="comment-author">{{ comment.user?.nickname || '匿名用户' }}</span>
              <span class="comment-time">{{ formatDate(comment.createdAt) }}</span>
            </div>
            <div class="comment-content">{{ comment.content }}</div>
          </div>
        </div>

        <div v-else class="empty-comments">
          <el-empty description="暂无评论，快来发表第一条评论吧"></el-empty>
        </div>

        <el-pagination
          v-if="totalComments > 0"
          :current-page="currentPage"
          :page-size="pageSize"
          :total="totalComments"
          @current-change="handlePageChange"
          layout="prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </section>
      </div>

      <aside class="sidebar">
        <div class="sidebar-section">
          <h3>近期文章</h3>
          <ul class="recent-articles">
            <li v-for="article in recentArticles" :key="article.id">
              <a @click="goArticle(article.id)">{{ article.title }}</a>
            </li>
          </ul>
        </div>
        <div class="sidebar-section">
          <h3>分类列表</h3>
          <ul class="category-list">
            <li v-for="category in categories" :key="category.id">
              <a @click="goCategory(category.id)">{{ category.name }}</a>
            </li>
          </ul>
        </div>
        <div class="sidebar-section" v-if="articleTags && articleTags.length > 0">
          <h3>标签云</h3>
          <div class="tag-cloud">
            <el-tag 
              v-for="tag in articleTags" 
              :key="tag.id" 
              size="mini"
              @click="goTag(tag.id)"
            >
              {{ tag.name }}
            </el-tag>
          </div>
        </div>
      </aside>
    </main>

    <footer class="blog-footer">
      <p>© 2026 光惠激光技术博客. All rights reserved.</p>
    </footer>

    <ShareModal
      :visible="shareModalVisible"
      :title="article?.title"
      :url="currentUrl"
      :description="article?.summary"
      @close="handleShareClose"
    />

    <Footer />
  </div>
</template>

<script>
import BlogHeader from '@/components/BlogHeader.vue'
import ShareModal from '@/components/ShareModal.vue'
import Footer from '@/components/Footer/index.vue'
import { getArticle, getCategories, getTags, getRecentArticles, getArticleComments, createComment, incrementView, toggleLike, checkLiked } from '@/api/blog'
import getFingerprint from '@/utils/fingerprint'
import { marked } from 'marked'

export default {
  name: 'ArticleDetail',
  components: {
    BlogHeader,
    ShareModal,
    Footer
  },
  data() {
    return {
      article: null,
      articleTags: [],
      categories: [],
      recentArticles: [],
      comments: [],
      currentPage: 1,
      pageSize: 10,
      totalComments: 0,
      commentContent: '',
      isAnonymous: false,
      isLiked: false,
      prevArticle: null,
      nextArticle: null,
      renderedContent: '',
      shareModalVisible: false,
      currentUrl: ''
    }
  },
  mounted() {
    this.currentUrl = window.location.href
    this.loadArticle()
    this.loadSidebarData()
    this.loadComments()
  },
  watch: {
    '$route.params.id'() {
      window.scrollTo(0, 0)
      this.comments = []
      this.currentPage = 1
      this.prevArticle = null
      this.nextArticle = null
      this.articleTags = []
      this.isLiked = false
      this.loadArticle()
      this.loadComments()
    }
  },
  methods: {
    async loadArticle() {
      const id = this.$route.params.id
      try {
        const response = await getArticle(id)
        this.article = response.data
        this.renderedContent = this.article.contentType === 'markdown' 
          ? marked(this.article.content)
          : this.article.content
        
        const fp = getFingerprint()
        await Promise.all([
          incrementView(id, fp),
          checkLiked(id, fp).then(res => { this.isLiked = res.data }).catch(() => {})
        ])
        
        this.article.readCount++
        
        this.loadTags()
        this.loadNavArticles()
      } catch (error) {
        console.error('Failed to load article:', error)
      }
    },
    async loadSidebarData() {
      try {
        const [categoriesRes, recentRes] = await Promise.all([
          getCategories(),
          getRecentArticles()
        ])
        this.categories = categoriesRes.data
        this.recentArticles = recentRes.data.filter(a => a.id !== parseInt(this.$route.params.id))
      } catch (error) {
        console.error('Failed to load sidebar data:', error)
      }
    },
    async loadTags() {
      try {
        const response = await getTags()
        const allTags = response.data
        if (this.article.tagIds) {
          const ids = this.article.tagIds.split(',').map(id => parseInt(id))
          this.articleTags = allTags.filter(tag => ids.includes(tag.id))
        }
      } catch (error) {
        console.error('Failed to load tags:', error)
      }
    },
    async loadNavArticles() {
      try {
        const response = await getRecentArticles()
        const articles = response.data
        const currentIndex = articles.findIndex(a => a.id === this.article.id)
        if (currentIndex > 0) {
          this.prevArticle = articles[currentIndex - 1]
        }
        if (currentIndex < articles.length - 1) {
          this.nextArticle = articles[currentIndex + 1]
        }
      } catch (error) {
        console.error('Failed to load nav articles:', error)
      }
    },
    async loadComments() {
      const articleId = this.$route.params.id
      try {
        const response = await getArticleComments(articleId, this.currentPage - 1, this.pageSize)
        this.comments = response.data.content
        this.totalComments = response.data.totalElements
      } catch (error) {
        console.error('Failed to load comments:', error)
      }
    },
    async submitComment() {
      if (!this.commentContent.trim()) {
        this.$message.warning('请输入评论内容')
        return
      }

      const data = {
        articleId: parseInt(this.$route.params.id),
        content: this.commentContent,
        fingerprint: getFingerprint(),
        userId: this.isAnonymous ? null : null
      }

      try {
        await createComment(data)
        this.$message.success('评论提交成功')
        this.commentContent = ''
        this.currentPage = 1
        await this.loadComments()
        this.article.commentCount = this.totalComments
      } catch (error) {
        console.error('Failed to submit comment:', error)
        this.$message.error('评论提交失败')
      }
    },
    async handleLike() {
      const articleId = parseInt(this.$route.params.id)
      const fingerprint = getFingerprint()
      try {
        const response = await toggleLike(articleId, fingerprint)
        this.isLiked = response.data
        this.article.likeCount += this.isLiked ? 1 : -1
      } catch (error) {
        console.error('Failed to like:', error)
      }
    },
    handleSearchFromHeader(keyword) {
      this.$router.push({ path: '/', query: { keyword } })
    },
    // handleFavorite() {
    //   this.$message.info('收藏功能开发中')
    // },
    handleComment() {
      document.querySelector('.comment-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    handleShare() {
      this.shareModalVisible = true
    },
    handleShareClose() {
      this.shareModalVisible = false
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadComments()
    },
    goHome() {
      this.$router.push('/')
    },
    goCategories() {
      this.$router.push('/categories')
    },
    goTags() {
      this.$router.push('/tags')
    },
    goArticle(id) {
      this.$router.push(`/article/${id}`)
    },
    goCategory(id) {
      this.$router.push({ path: '/', query: { categoryId: id } })
    },
    goTag(id) {
      this.$router.push({ path: '/', query: { tagId: id } })
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    },
    generateFingerprint() {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('fingerprint', 0, 0)
      return canvas.toDataURL()
    }
  }
}
</script>

<style scoped>
.article-detail-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  max-width: 1200px;
  width: 100%;
  margin: 2rem auto;
  display: flex;
  gap: 2rem;
  padding: 0 1.5rem;
  flex: 1;
  box-sizing: border-box;
}

.article-content-col {
  flex: 1;
  min-width: 0;
  width: 0;
}

.article-content {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.article-title {
  font-size: 1.8rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.article-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.article-meta .meta-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.article-meta .category {
  background: #dbeafe;
  color: #1d4ed8;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;
}

.article-meta .category:hover {
  background: #bfdbfe;
}

.read-count {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.article-tags {
  margin-bottom: 1.5rem;
}

.article-tags .el-tag{
  margin-right: 8px;
  margin-bottom: 8px;
  cursor: pointer;
}

.article-body {
  line-height: 1.8;
  color: #374151;
  font-size: 1rem;
  overflow: hidden;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.article-body h1,
.article-body h2,
.article-body h3,
.article-body h4,
.article-body h5,
.article-body h6 {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #1f2937;
}

.article-body p {
  margin-bottom: 1rem;
}

.article-body img{
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}
.article-body ::v-deep img {
  max-width: 100% !important;
  height: auto;
  border-radius: 8px;
  margin: 10px 0;
}

.article-body code {
  background: #f3f4f6;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: 'Consolas', monospace;
}

.article-body pre {
  background: #1f2937;
  color: #e5e7eb;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  margin: 1rem 0;
  max-width: 100%;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.article-body blockquote {
  border-left: 4px solid #3b82f6;
  padding-left: 1rem;
  margin: 1rem 0;
  color: #6b7280;
  background: #f9fafb;
  padding: 1rem;
  border-radius: 0 8px 8px 0;
}

.article-actions {
  margin: 2rem 0;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.action-buttons {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 20px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  color: #6b7280;
}

.action-btn:hover {
  color: #3b82f6;
}

.action-btn.liked {
  color: #f56c6c;
}

.action-btn.liked span {
  color: #f56c6c;
}

.article-nav {
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
  gap: 1rem;
}

.nav-item {
  flex: 1;
  min-width: 0;
}

.nav-item.prev {
  text-align: left;
}

.nav-item.next {
  text-align: right;
}

.nav-label {
  display: block;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-bottom: 0.25rem;
}

.nav-item a {
  color: #374151;
  text-decoration: none;
  font-size: 0.875rem;
  cursor: pointer;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-item a:hover {
  color: #3b82f6;
}

.sidebar {
  width: 280px;
  flex-shrink: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.sidebar-section {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.sidebar-section h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e5e7eb;
  color: #374151;
}

.category-list,
.recent-articles {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  max-width: 100%;
}

.category-list li,
.recent-articles li {
  margin-bottom: 0.75rem;
  width: 100%;
  max-width: 100%;
}

.category-list a,
.recent-articles a {
  color: #374151;
  text-decoration: none;
  font-size: 0.9rem;
  line-height: 1.4;
  cursor: pointer;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.category-list a:hover,
.recent-articles a:hover {
  color: #3b82f6;
}

.tag-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.tag-cloud .el-tag {
  background: white;
  cursor: pointer;
}

.tag-cloud .el-tag:hover {
  background: #dbeafe;
  cursor: pointer;
}

.comment-section {
  margin-top: 1.5rem;
}

.comment-container {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.comment-container h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #1f2937;
}

.comment-input {
  margin-bottom: 1.5rem;
}

.submit-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.anonymous-checkbox {
  font-size: 0.875rem;
  color: #6b7280;
  cursor: pointer;
}

.comment-item {
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.comment-item:last-child {
  border-bottom: none;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.comment-author {
  font-weight: 500;
  color: #1f2937;
}

.comment-time {
  font-size: 0.75rem;
  color: #9ca3af;
}

.comment-content {
  color: #4b5563;
  line-height: 1.6;
  word-break: break-word;
}

.empty-comments {
  padding:0 1rem;
  text-align: center;
}
  

.el-pagination {
  margin-top: 1.5rem;
  text-align: center;
}

.blog-footer {
  background: #0b193f;
  color: #9ca3af;
  text-align: center;
  padding: 1.5rem;
  margin-top: auto;
}

.blog-footer p {
  margin: 0;
  font-size: 0.875rem;
}

/* ========== 响应式屏幕适配 ========== */

/* 中等屏幕（1025px - 1400px） */
@media screen and (max-width: 1200px) {
  .main-content {
    gap: 1.5rem;
    padding: 0 1rem;
  }
  
  .sidebar {
    width: 260px;
  }
  
  .article-content {
    padding: 1.5rem;
  }
  
  .article-title {
    font-size: 1.6rem;
  }
}

/* 小屏幕/笔记本（769px - 1024px） */
@media screen and (max-width: 1024px) {
  .main-content {
    gap: 1.5rem;
  }
  
  .article-title {
    font-size: 1.5rem;
  }
  
  .comment-container {
    padding: 1.5rem;
  }
}

/* 平板（577px - 768px） */
@media screen and (max-width: 768px) {
  .main-content {
    flex-direction: column;
    gap: 1.5rem;
    margin: 1rem auto;
    padding: 0 1rem;
  }

  .article-content-col {
    width: 100%;
  }

  .sidebar {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    padding: 0;
  }

  .sidebar-section {
    margin-bottom: 0;
    padding: 0.85rem;
  }

  .sidebar-section h3 {
    font-size: 0.95rem;
    margin-bottom: 0.75rem;
    padding-bottom: 0.4rem;
  }

  .recent-articles li,
  .category-list li {
    margin-bottom: 0.6rem;
  }

  .recent-articles a,
  .category-list a {
    font-size: 0.85rem;
  }

  .tag-cloud {
    gap: 0.4rem;
  }

  .tag-cloud .el-tag {
    font-size: 12px !important;
    height: 24px !important;
    line-height: 24px !important;
    padding: 0 8px !important;
  }
  
  .article-content {
    padding: 1.25rem;
  }
  
  .article-title {
    font-size: 1.4rem;
  }
  
  .article-meta {
    gap: 1rem;
    font-size: 0.8rem;
  }
  
  .article-body {
    font-size: 0.95rem;
  }
  
  .action-buttons {
    gap: 1rem;
  }
  
  .action-btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
  
  .comment-container {
    padding: 1.25rem;
  }
  
  .comment-container h3 {
    font-size: 1.1rem;
  }
  
  .article-nav {
    flex-direction: column;
    gap: 1rem;
  }
  
  .nav-item.prev,
  .nav-item.next {
    text-align: left;
  }
}

/* 小平板（≤ 600px）侧边栏单列 */
@media screen and (max-width: 600px) {
  .sidebar {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
}

/* 手机（≤ 576px） */
@media screen and (max-width: 576px) {
  .main-content {
    margin: 0.75rem auto;
    padding: 0 0.75rem;
  }
  
  .article-content {
    padding: 1rem;
    border-radius: 6px;
  }
  
  .article-title {
    font-size: 1.2rem;
  }
  
  .article-meta {
    gap: 0.75rem;
    font-size: 0.7rem;
  }
  
  .article-meta .category {
    padding: 0.2rem 0.5rem;
  }
  
  .article-tags .el-tag {
    font-size: 10px !important;
    margin-right: 4px;
    margin-bottom: 4px;
  }
  
  .article-body {
    font-size: 0.9rem;
    line-height: 1.7;
  }
  
  .article-body pre {
    font-size: 0.8rem;
    padding: 0.75rem;
  }
  
  .action-buttons {
    gap: 0.75rem;
  }
  
  .action-btn {
    padding: 0.3rem 0.6rem;
    font-size: 0.75rem;
  }
  
  .comment-container {
    padding: 1rem;
  }
  
  .comment-container h3 {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
  
  .submit-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .submit-row .el-button{
    width: 100%;
  }
  .comment-item {
    padding: 0.75rem;
  }
  
  .comment-author {
    font-size: 0.85rem;
  }
  
  .comment-content {
    font-size: 0.85rem;
  }
  
 .empty-comments {
    padding: 0;
  } 
    .empty-comments ::v-deep .el-empty__image{
    width: 80px !important;
  }
  .el-pagination {
    margin-top: 1rem;
  }
}

/* 小手机（≤ 375px） */
@media screen and (max-width: 375px) {
  .article-title {
    font-size: 1rem;
  }
  
  .article-content {
    padding: 0.75rem;
  }
  
  .article-meta {
    gap: 0.5rem;
  }
  
  .action-buttons {
    gap: 0.5rem;
  }
  
  .action-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
  }
    .empty-comments ::v-deep .el-empty__image{
    width: 80px !important;
  }
  .comment-container {
    padding: 0.75rem;
  }
}
</style>
